"use client";

import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Icon } from "@calcom/ui/components/icon";
import Link from "next/link";

// Deliberately not exported from @calcom/lib/constants, where sibling URLs like POWERED_BY_URL
// live. Thirty test files mock that module with explicit object literals, so every export added
// to it breaks any test that renders a consumer without updating its mock — a diff this fork
// would re-resolve on every upstream merge. Next inlines NEXT_PUBLIC_* at build time, so reading
// it here costs nothing and keeps the change inside a file upstream does not have.
//
// `process` must stay a free variable: webpack's DefinePlugin only substitutes free identifiers,
// so importing it from "node:process" leaves the lookup in the client bundle against a shim whose
// env is empty, silently falling back to the upstream URL below.
const SOURCE_URL = process.env.NEXT_PUBLIC_AGPL_SOURCE_URL || "https://github.com/calcom/cal.com/tree/v6.2.0";

type SourceLinkProps = {
  /** `sidebar` keeps the dashboard credits slot; default is the public-page corner badge. */
  variant?: "fixed" | "sidebar";
};

/**
 * AGPLv3 §13 offer of Corresponding Source. Points at a landing page (`/legal/source`)
 * rather than the archive itself — the archive is only reachable from a button's click
 * handler there, not a static href, so a plain crawler following links never fetches it.
 *
 * Rendered on entry points every user of this instance passes through (public booking
 * page, login page, signed-in dashboard), not gated behind auth — the AGPLv3 obligation
 * to offer this covers anyone interacting with the instance over the network.
 */
const SourceLink = ({ variant = "fixed" }: SourceLinkProps) => {
  const { t } = useLocale();
  const year = new Date().getFullYear();
  const label = `© ${year} · ${t("agpl_source_link_label")}`;

  if (variant === "sidebar") {
    return (
      <small className="text-default mx-3 mb-2 mt-1 hidden text-[0.5rem] opacity-50 lg:block">
        <Link
          href={SOURCE_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-0.5 hover:underline">
          {label}
          <Icon name="external-link" className="h-2 w-2" />
        </Link>
      </small>
    );
  }

  return (
    <Link
      href={SOURCE_URL}
      target="_blank"
      rel="noreferrer"
      className="fixed right-4 bottom-16 z-40 flex items-center gap-1 text-subtle text-xs hover:text-emphasis sm:bottom-4">
      <span>{label}</span>
      <Icon name="external-link" className="h-3 w-3" />
    </Link>
  );
};

export default SourceLink;
